import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  CircleCheck,
  ClipboardList,
  Loader2,
  RotateCw,
  TriangleAlert,
} from "lucide-react";
import {
  RECEIVE_RETURNS_PERMISSION,
  type ReturnReceipt,
  type ReturnReceiptLine,
  type WriteRejection,
} from "@estelle/domain";
import { useState, type ReactNode } from "react";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { dateTime } from "@/modules/orders/format";
import { RejectionNotice } from "@/modules/orders/screens/RejectionNotice";

/**
 * A receipt, read back: what was counted, what Shopify has accepted, what is
 * still pending and why.
 *
 * One component for every place a receipt is shown — the receiving screen after
 * Confirm, the same screen when a parcel is opened again, and the order's own
 * page — so the three cannot describe one receipt three ways. It draws the
 * server's rows and nothing else: a line is "restocked" because the server says
 * Shopify accepted it, never because the form said so.
 *
 * Retry and the follow-up task need `orders.returns.receive`. Hidden without it
 * as a courtesy; the API checks regardless.
 */

const STATUS: Record<
  ReturnReceipt["status"],
  { label: string; tone: "ok" | "wait" }
> = {
  complete: { label: "Return received", tone: "ok" },
  restock_pending: { label: "Received, restock pending", tone: "wait" },
  tag_pending: { label: "Received, tag pending", tone: "wait" },
};

export function StatusPill({
  tone,
  children,
}: {
  tone: "ok" | "wait" | "bad";
  children: ReactNode;
}) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1.5 font-medium",
        tone === "ok" &&
          "border-transparent bg-tint-green text-tint-green-foreground",
        tone === "wait" &&
          "border-transparent bg-tint-yellow text-tint-yellow-foreground",
        tone === "bad" &&
          "border-destructive/40 bg-destructive/10 text-destructive",
      )}
    >
      <span aria-hidden className="size-1.5 rounded-full bg-current" />
      {children}
    </Badge>
  );
}

/** A task, opened over the Tasks list the way a linked task is from an order. */
function TaskLink({
  taskId,
  children,
}: {
  taskId: string;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="text-primary-strong underline-offset-2 hover:underline"
      onClick={() => {
        void navigate({ to: "/tasks/all", search: { record: taskId } });
      }}
    >
      {children}
    </button>
  );
}

function Photos({
  receiptId,
  line,
}: {
  receiptId: string;
  line: ReturnReceiptLine;
}) {
  const data = useDataSource();
  if (line.photos.length === 0) return null;
  return (
    <ul className="mt-1.5 flex flex-wrap gap-2">
      {line.photos.map((photo) => {
        const url = data.returns.photoUrl(receiptId, photo.id);
        return (
          <li key={photo.id}>
            {url ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="block size-16 overflow-hidden rounded-md border"
                aria-label={`Photo of ${line.title}: ${photo.filename}`}
              >
                <img
                  src={url}
                  alt={`Damaged ${line.title}`}
                  loading="lazy"
                  className="size-full object-cover"
                />
              </a>
            ) : (
              <span className="text-muted-foreground text-xs">
                {photo.filename}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function RestockStatus({ line }: { line: ReturnReceiptLine }) {
  if (line.good === 0) return null;
  // Another request is writing it: not a failure, and not something to retry.
  if (line.restockInProgress) {
    return (
      <p
        role="status"
        className="text-muted-foreground flex items-center gap-1.5 text-xs"
      >
        <Loader2 className="size-3.5 animate-spin" aria-hidden />
        Being put back in stock: +{line.good}
      </p>
    );
  }
  if (line.restockStatus === "accepted") {
    return (
      <p className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300">
        <CircleCheck className="size-3.5" aria-hidden />
        Restocked +{line.good}: Shopify accepted it
        {line.restockError ? ` (${line.restockError})` : ""}
      </p>
    );
  }
  if (line.restockStatus === "not_needed") {
    return (
      <p className="text-muted-foreground text-xs">
        No stock item on this line, so nothing to put back.
      </p>
    );
  }
  return (
    <p
      role="status"
      className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-300"
    >
      <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
      <span>
        Restock pending: +{line.good} not in stock yet.
        {line.restockError ? ` ${line.restockError}` : ""}
        {line.restockAttempts > 0
          ? ` (${String(line.restockAttempts)} attempt${line.restockAttempts === 1 ? "" : "s"})`
          : ""}
      </span>
    </p>
  );
}

function LineResult({
  receiptId,
  line,
}: {
  receiptId: string;
  line: ReturnReceiptLine;
}) {
  return (
    <li className="space-y-1.5 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <p className="min-w-0 text-sm font-medium">
          {line.title}
          {line.variantTitle ? (
            <span className="text-muted-foreground font-normal">
              {" "}
              — {line.variantTitle}
            </span>
          ) : null}
        </p>
        <p className="text-muted-foreground shrink-0 text-xs">
          of {line.quantity}
        </p>
      </div>
      {line.sku ? (
        <p className="text-muted-foreground text-xs">SKU {line.sku}</p>
      ) : null}
      <div className="flex flex-wrap gap-1.5">
        {line.good > 0 ? (
          <StatusPill tone="ok">Good {line.good}</StatusPill>
        ) : null}
        {line.damaged > 0 ? (
          <StatusPill tone="bad">Damaged {line.damaged}</StatusPill>
        ) : null}
        {line.missing > 0 ? (
          <StatusPill tone="wait">Missing {line.missing}</StatusPill>
        ) : null}
      </div>
      <RestockStatus line={line} />
      {line.damaged > 0 ? (
        <p className="text-sm">
          <span className="text-muted-foreground">Damaged: </span>
          {line.damagedReason ?? "No reason given"}
        </p>
      ) : null}
      {line.missing > 0 ? (
        <p className="text-sm">
          <span className="text-muted-foreground">Missing: </span>
          {line.missingReason ?? "No reason given"}
        </p>
      ) : null}
      <Photos receiptId={receiptId} line={line} />
    </li>
  );
}

export function ReceiptView({
  receipt,
  onChanged,
  showNoUndo = false,
  justReceivedBy,
  className,
}: {
  receipt: ReturnReceipt;
  /** Called with the receipt the server answers after a retry or a follow-up. */
  onChanged?: (receipt: ReturnReceipt) => void;
  /** Say, once, that there is no undo: on the screen that has just confirmed. */
  showNoUndo?: boolean;
  /**
   * Set on the screen of a person whose own confirm was refused because someone
   * else had just received the parcel: it says so, by name, above the receipt.
   */
  justReceivedBy?: string;
  className?: string;
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const canAct = can(permissions, RECEIVE_RETURNS_PERMISSION);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);
  const [followUp, setFollowUp] = useState<string | null>(null);

  const settled = () => {
    void queryClient.invalidateQueries({ queryKey: ["returns"] });
    void queryClient.invalidateQueries({ queryKey: ["orders"] });
    void queryClient.invalidateQueries({ queryKey: ["inventory"] });
  };

  const retry = useMutation({
    mutationFn: () => data.returns.retry(receipt.id),
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        onChanged?.(result.data);
        settled();
      } else {
        setRejection(result.rejection);
      }
    },
  });

  const task = useMutation({
    mutationFn: () => data.returns.createFollowUp(receipt.id),
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        setFollowUp(result.data.taskId);
        settled();
      } else {
        setRejection(result.rejection);
      }
    },
  });

  const status = STATUS[receipt.status];
  const lost = receipt.lines.some((line) => line.damaged + line.missing > 0);
  const followUpTaskId = receipt.followUpTaskId ?? followUp;
  // Something is still being written by another request: say so, keep the
  // screen moving (the callers refresh it), and offer no Retry until it is
  // refused or its claim has gone stale (#558).
  const pending = receipt.status !== "complete" && !receipt.processing;

  return (
    <section
      aria-label={`Receipt for ${receipt.shipmentName ?? receipt.orderNumber}`}
      className={cn("space-y-3", className)}
    >
      {justReceivedBy ? (
        <p
          role="status"
          className="rounded-md border border-transparent bg-tint-blue p-3 text-sm"
        >
          <strong>{justReceivedBy} has just received this parcel.</strong>{" "}
          Nothing you counted was saved, so nothing is doubled. What follows is
          their receipt
          {receipt.processing ? ", still being written to Shopify" : ""}.
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        {receipt.processing ? (
          <StatusPill tone="wait">
            <Loader2 className="size-3 animate-spin" aria-hidden />
            Being written to Shopify
          </StatusPill>
        ) : (
          <StatusPill tone={status.tone}>{status.label}</StatusPill>
        )}
        <span className="text-muted-foreground text-sm">
          {receipt.shipmentName ?? receipt.orderNumber}
        </span>
      </div>
      <p className="text-muted-foreground text-sm">
        Received by {receipt.receivedByName ?? "someone"}{" "}
        {dateTime(receipt.receivedAt)}
        {receipt.warehouseName
          ? ` · good items put back at ${receipt.warehouseName}`
          : ""}
        {receipt.courierMarkedDelivered
          ? ""
          : " · the courier had not marked it RTO delivered"}
      </p>
      {receipt.note ? (
        <p className="bg-muted/50 rounded-md border px-3 py-2 text-sm">
          {receipt.note}
        </p>
      ) : null}

      <ul className="divide-y rounded-md border px-3">
        {receipt.lines.map((line) => (
          <LineResult key={line.id} receiptId={receipt.id} line={line} />
        ))}
      </ul>

      <div className="space-y-1.5 text-sm">
        {receipt.tagStatus === "accepted" ? (
          <p className="flex items-center gap-1.5">
            <CircleCheck
              className="size-4 text-emerald-600 dark:text-emerald-400"
              aria-hidden
            />
            Order tagged <Badge variant="outline">RTO Received</Badge> in
            Shopify.
          </p>
        ) : receipt.tagInProgress ? (
          <p
            role="status"
            className="text-muted-foreground flex items-center gap-1.5"
          >
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Adding the tag <Badge variant="outline">RTO Received</Badge> in
            Shopify
          </p>
        ) : (
          <p
            role="status"
            className="flex items-start gap-1.5 text-amber-700 dark:text-amber-300"
          >
            <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>
              Tag RTO Received is not on the order in Shopify yet.
              {receipt.tagError ? ` ${receipt.tagError}` : ""}
            </span>
          </p>
        )}

        {receipt.prepaid ? (
          <p className="flex items-start gap-1.5">
            <ClipboardList
              className="text-muted-foreground mt-0.5 size-4 shrink-0"
              aria-hidden
            />
            <span>
              <strong>Prepaid: refund needed.</strong> The refund is done in
              Shopify; the ERP never moves money.{" "}
              {receipt.refundTaskId ? (
                <>
                  Task{" "}
                  <TaskLink taskId={receipt.refundTaskId}>
                    Refund order {receipt.orderNumber}
                  </TaskLink>{" "}
                  was created.
                </>
              ) : null}
            </span>
          </p>
        ) : null}

        {receipt.restockFlags.map((flag) => (
          <p
            key={flag.id}
            role="alert"
            className="border-destructive/30 bg-destructive/5 flex items-start gap-1.5 rounded-md border p-2 text-sm"
          >
            <TriangleAlert
              className="text-destructive mt-0.5 size-4 shrink-0"
              aria-hidden
            />
            <span>
              <strong>Possible double restock.</strong> Shopify restocked{" "}
              {flag.restockedQuantity} unit
              {flag.restockedQuantity === 1 ? "" : "s"}
              {flag.refundedAt ? (
                <> on {dateTime(flag.refundedAt)}</>
              ) : null}{" "}
              after this return was received. Check the stock.{" "}
              {flag.taskId ? (
                <TaskLink taskId={flag.taskId}>Follow-up task</TaskLink>
              ) : null}
            </span>
          </p>
        ))}
      </div>

      {rejection ? (
        <RejectionNotice
          rejection={rejection}
          onReload={() => {
            setRejection(null);
            settled();
          }}
        />
      ) : null}

      {canAct && (pending || lost) ? (
        <div className="flex flex-col gap-2 sm:flex-row">
          {pending ? (
            <Button
              type="button"
              className="max-sm:h-11 pointer-coarse:h-11"
              disabled={retry.isPending}
              onClick={() => {
                retry.mutate();
              }}
            >
              {retry.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                <RotateCw />
              )}
              Retry
            </Button>
          ) : null}
          {lost ? (
            followUpTaskId ? (
              <p className="flex items-center gap-1.5 text-sm">
                <ClipboardList className="size-4" aria-hidden />
                <TaskLink taskId={followUpTaskId}>
                  Follow-up task created
                </TaskLink>
              </p>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="max-sm:h-11 pointer-coarse:h-11"
                disabled={task.isPending}
                onClick={() => {
                  task.mutate();
                }}
              >
                {task.isPending ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <ClipboardList />
                )}
                Create follow-up task
              </Button>
            )
          ) : null}
        </div>
      ) : null}

      {showNoUndo ? (
        <p className="text-muted-foreground text-xs">
          The parcel has left the list. There is no undo in this version: a
          mistake is corrected with a normal stock adjustment and a note on the
          receipt.
        </p>
      ) : null}
    </section>
  );
}
